import React, { createContext, useContext, useCallback, useRef, useMemo } from 'react';
import { useSyncExternalStore } from 'react';

/**
 * 任务进度状态接口
 * 用于追踪 Feed 流页面的创作任务进度
 */
interface TaskProgressState {
  // 生命周期是否活跃（用户是否离开了首屏）
  lifecycleActive: boolean;
  // 当前生命周期内已完成的任务数
  completedCount: number;
  // 当前生命周期内的总任务数
  totalCount: number;
  // 正在进行中的任务 ID 集合
  pendingTaskIds: Set<string>;
}

/**
 * Context 值接口
 */
interface TaskProgressContextValue {
  // 状态
  state: TaskProgressState;
  // 添加任务（任务创建时调用）
  addTask: (taskId: string) => void;
  // 完成任务（任务完成时调用）
  completeTask: (taskId: string) => void;
  // 开始生命周期（离开首屏时调用）
  startLifecycle: () => void;
  // 结束生命周期（回到首屏时调用）
  endLifecycle: () => void;
  // 检查任务是否在追踪中
  isTaskPending: (taskId: string) => boolean;
}

// 创建 Context
const TaskProgressContext = createContext<TaskProgressContextValue | null>(null);

// 初始状态
const createInitialState = (): TaskProgressState => ({
  lifecycleActive: false,
  completedCount: 0,
  totalCount: 0,
  pendingTaskIds: new Set(),
});

/**
 * 创建状态存储（使用 useSyncExternalStore 模式）
 */
function createTaskProgressStore() {
  let state = createInitialState();
  const listeners = new Set<() => void>();

  const getState = () => state;

  const setState = (newState: Partial<TaskProgressState>) => {
    state = { ...state, ...newState };
    listeners.forEach(listener => listener());
  };

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  return { getState, setState, subscribe };
}

// 全局 store 实例（用于非 React 组件访问）
let globalStore: ReturnType<typeof createTaskProgressStore> | null = null;

/**
 * 获取全局 store 实例
 * 用于在非 React 组件中访问任务进度
 */
export const getTaskProgressStore = () => {
  if (!globalStore) {
    globalStore = createTaskProgressStore();
  }
  return globalStore;
};

/**
 * 全局添加任务函数（可在非 React 组件中调用）
 */
export const globalAddTask = (taskId: string) => {
  const store = getTaskProgressStore();
  const currentState = store.getState();

  console.log(`[TaskProgress] globalAddTask 被调用: ${taskId}`, {
    pendingTaskIds: Array.from(currentState.pendingTaskIds),
    alreadyExists: currentState.pendingTaskIds.has(taskId),
    lifecycleActive: currentState.lifecycleActive,
    totalCount: currentState.totalCount,
  });

  if (currentState.pendingTaskIds.has(taskId)) {
    console.warn(`[TaskProgress] 任务 ${taskId} 已存在，跳过添加`);
    return;
  }

  const newPendingTaskIds = new Set(currentState.pendingTaskIds);
  newPendingTaskIds.add(taskId);

  if (currentState.lifecycleActive) {
    store.setState({
      pendingTaskIds: newPendingTaskIds,
      totalCount: currentState.totalCount + 1,
    });
    console.log(`[TaskProgress] 任务已添加(生命周期内): ${taskId}, 总数: ${currentState.totalCount + 1}`);
  } else {
    store.setState({
      pendingTaskIds: newPendingTaskIds,
    });
    console.log(`[TaskProgress] 任务已添加(等待中): ${taskId}, 待处理数: ${newPendingTaskIds.size}`);
  }
};

/**
 * 全局完成任务函数（可在非 React 组件中调用）
 */
export const globalCompleteTask = (taskId: string) => {
  const store = getTaskProgressStore();
  const currentState = store.getState();

  console.log(`[TaskProgress] globalCompleteTask 被调用: ${taskId}`, {
    pendingTaskIds: Array.from(currentState.pendingTaskIds),
    hasTask: currentState.pendingTaskIds.has(taskId),
    lifecycleActive: currentState.lifecycleActive,
    completedCount: currentState.completedCount,
    totalCount: currentState.totalCount,
  });

  if (!currentState.pendingTaskIds.has(taskId)) {
    console.warn(`[TaskProgress] 任务 ${taskId} 不在 pendingTaskIds 中，跳过完成处理`);
    return;
  }

  const newPendingTaskIds = new Set(currentState.pendingTaskIds);
  newPendingTaskIds.delete(taskId);

  if (currentState.lifecycleActive) {
    const newCompletedCount = currentState.completedCount + 1;

    // 检查是否所有任务都已完成
    if (newCompletedCount >= currentState.totalCount && currentState.totalCount > 0) {
      // 所有任务完成，清零计数，但保持生命周期活跃
      store.setState({
        pendingTaskIds: newPendingTaskIds,
        completedCount: 0,
        totalCount: 0,
      });
      console.log(`[TaskProgress] 所有任务已完成，计数已归零`);
    } else {
      store.setState({
        pendingTaskIds: newPendingTaskIds,
        completedCount: newCompletedCount,
      });
      console.log(`[TaskProgress] 任务已完成: ${taskId}, 完成数: ${newCompletedCount}/${currentState.totalCount}`);
    }
  } else {
    store.setState({
      pendingTaskIds: newPendingTaskIds,
    });
    console.log(`[TaskProgress] 任务已完成(等待中): ${taskId}, 待处理数: ${newPendingTaskIds.size}`);
  }
};

/**
 * TaskProgressProvider 组件
 */
export const TaskProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 使用全局 store
  const storeRef = useRef(getTaskProgressStore());
  const store = storeRef.current;

  // 使用 useSyncExternalStore 订阅状态变化
  const state = useSyncExternalStore(store.subscribe, store.getState);

  /**
   * 添加任务
   * 在任务创建时调用，将任务添加到追踪列表
   */
  const addTask = useCallback((taskId: string) => {
    globalAddTask(taskId);
  }, []);

  /**
   * 完成任务
   * 在任务完成时调用，更新完成计数
   */
  const completeTask = useCallback((taskId: string) => {
    globalCompleteTask(taskId);
  }, []);

  /**
   * 开始生命周期
   * 当用户滚动离开首屏时调用
   */
  const startLifecycle = useCallback(() => {
    const currentState = store.getState();

    // 如果已经在生命周期内，不重复开始
    if (currentState.lifecycleActive) {
      return;
    }

    // 滚动离开首屏时，始终开始生命周期（显示"回到顶部"按钮）
    // 如果有未完成任务，则同时显示进度
    store.setState({
      lifecycleActive: true,
      totalCount: currentState.pendingTaskIds.size,
      completedCount: 0,
    });

    console.log(`[TaskProgress] 生命周期开始, 任务数: ${currentState.pendingTaskIds.size}`);
  }, [store]);

  /**
   * 结束生命周期
   * 当用户回到首屏时调用
   */
  const endLifecycle = useCallback(() => {
    const currentState = store.getState();

    if (!currentState.lifecycleActive) {
      return;
    }

    store.setState({
      lifecycleActive: false,
      totalCount: 0,
      completedCount: 0,
    });

    console.log(`[TaskProgress] 生命周期结束`);
  }, [store]);

  /**
   * 检查任务是否在追踪中
   */
  const isTaskPending = useCallback((taskId: string) => {
    return store.getState().pendingTaskIds.has(taskId);
  }, [store]);

  // 使用 useMemo 避免每次渲染都创建新对象
  const value = useMemo<TaskProgressContextValue>(() => ({
    state,
    addTask,
    completeTask,
    startLifecycle,
    endLifecycle,
    isTaskPending,
  }), [state, addTask, completeTask, startLifecycle, endLifecycle, isTaskPending]);

  return (
    <TaskProgressContext.Provider value={value}>
      {children}
    </TaskProgressContext.Provider>
  );
};

/**
 * 自定义 Hook: 使用任务进度 Context
 */
export const useTaskProgress = (): TaskProgressContextValue => {
  const context = useContext(TaskProgressContext);
  if (!context) {
    throw new Error('useTaskProgress must be used within a TaskProgressProvider');
  }
  return context;
};

/**
 * 自定义 Hook: 获取进度显示状态
 * 返回是否应该显示进度条以及显示内容
 */
export const useTaskProgressDisplay = () => {
  const { state } = useTaskProgress();

  const shouldShowProgress = state.lifecycleActive && state.totalCount > 0;
  const isAllCompleted = state.completedCount >= state.totalCount && state.totalCount > 0;

  // 只有在生命周期活跃时才显示
  // 全部完成时只显示"回到顶部"，不显示进度
  const shouldShowProgressText = shouldShowProgress && !isAllCompleted;
  const shouldShowBackToTop = state.lifecycleActive;

  return {
    shouldShowProgress,
    shouldShowProgressText,
    shouldShowBackToTop,
    completedCount: state.completedCount,
    totalCount: state.totalCount,
    pendingCount: state.pendingTaskIds.size,
  };
};

export default TaskProgressContext;
